import * as React from "react";

import {cs}    from "@Common/react/chain-services";
import {Types} from "@Common/types";
import {chain} from "@Common/utils/fs";

import {DataTableD} from "./desktop/data-table-d";
import {Pagination} from "./pagination";
import {Sorting}    from "./sorting";

export interface Row {
    key       ?: string,
    className ?: string,
    columns    : Array<{
        domAttrs ?: React.HTMLAttributes<HTMLTableCellElement>,
        render    : () => React.ReactNode,
    }>,
    domAttrs  ?: React.HTMLAttributes<HTMLTableRowElement>,
    expansion ?: {
        render : (props:{colCount: number}) => React.ReactNode,
    }
}

/**
 * Direct use of DataTableD (desktop) with default support for pagination and sorting
 */
export const DataTableD1 = (
    {
        className,
        getDefaultSorting,
        paginationKey,
        darkRows="odd",
        pageSize,
        columns,
        rows,
        getFooter,
        getExtraRows,
        emptyAction,
        customSorting,
    }:{
        className         ?: string,
        columns            : Array<{
            align     ?: string,
            className ?: string,
            domAttrs  ?: React.HTMLAttributes<HTMLTableCellElement> & Record<string, string>,
            label     ?: string,
            render    ?: () => React.ReactNode,
            sortable  ?: {
                defaultDirection : Types.Common.UiComponents.Heavy.Common.Design2.DataTable.Sorting.Direction,
            },
            subTitle  ?: string,
            toolTip   ?: string,
            width     ?: string,
        }>,
        customSorting     ?: Types.Common.UiComponents.Heavy.Common.Design2.DataTable.Sorting.Next<Row>,
        darkRows          ?: "even" | "odd",
        emptyAction       ?: {
            onClick ?: React.MouseEventHandler,
            label   ?: string,
        },
        getDefaultSorting ?: () => Types.Common.UiComponents.Heavy.Common.Design2.DataTable.Sorting.Sort,
        getExtraRows      ?: (
            (props:{colCount: number, pagination: Types.Common.UiComponents.Heavy.Common.Design2.DataTable.Pagination.Next<Row>}) => Array<{
                className ?: string,
                color     ?: "light" | "dark",
                columns    : Array<{
                    className ?: string,
                    colSpan    : number,
                    render    ?: () => React.ReactNode,
                    ref       ?: React.Ref<HTMLTableCellElement>,
                }>,
                key       ?: string,
            }>
        ),
        getFooter         ?: (
            (props:{colCount: number, pagination: Types.Common.UiComponents.Heavy.Common.Design2.DataTable.Pagination.Next<Row>}) => {
                columns : Array<{
                    className ?: string,
                    colSpan   ?: number,
                    domAttrs  ?: React.HTMLAttributes<HTMLTableCellElement> & Record<string, string>,
                    render    ?: () => React.ReactNode,
                }>
            }
        ),
        pageSize          ?: number,
        /**
         * If this key is changed, the paging state (which page being shown) is reset
         */
        paginationKey     ?: string,
        rows               : Array<Row>,
    }
) => cs(
    ["sorting", (_, next) => customSorting ? next(customSorting) : Sorting({
        getDefault: getDefaultSorting,
        next,
    })],
    ["sortedRows", ({sorting}, next) => next(rows && chain(
        rows,
        (_) => sorting.apply(_, (row, columnIndex) => row.columns[columnIndex]?.sortValue()),
    ))],
    ["pagination", ({sorting, sortedRows}, next) => !pageSize ? next({active: false}) : Pagination({
        _key: JSON.stringify([paginationKey, sorting.value]),
        pageSize,
        rows: sortedRows,
        next,
    })],
    ["displayedRows", ({pagination, sortedRows}, next) => next(pagination?.active ? pagination.getDisplayedRows() : sortedRows)],
    ({sorting, pagination, displayedRows}) => DataTableD({
        rows: displayedRows,
        className,
        darkRows,
        columns: columns.map((col, i) => ({
            ...col,
            sorting: !col.sortable ? undefined : {
                direction: sorting.isSortingActiveForColumn({columnIndex: i}) && sorting.direction,
                toggle: () => {
                    sorting.toggleColumn({
                        columnIndex: i,
                        defaultDirection: col.sortable.defaultDirection,
                    });
                },
            },
        })),
        emptyAction,
        getExtraRows: getExtraRows && (({colCount}) => getExtraRows({colCount, pagination})),
        getFooter   : getFooter    && (({colCount}) => getFooter   ({colCount, pagination})),
    }),
);

